/**
 * How a report's `data_quality` block reads on a page (docs/architecture/data-quality.md):
 * the baseline notice, the coverage figure and the Not tracked row, in one place so every
 * page says the same thing. Everything here answers null — render nothing — for a field
 * the API did not send; nothing is ever shown as 0 in its place.
 */

import type { ConversionCredit, DataQuality, TrackingCoverage } from '@/api/dataQuality'
import { formatInteger } from '@/design'
import { formatDateKey } from '@/lib/filters'

/** Below this share of orders tracked, the coverage figure is shown amber. */
export const LOW_COVERAGE_PERCENT = 60

/** The row of orders with no tracked visit: never merged with Direct, always last. */
export const NOT_TRACKED_LABEL = 'Not tracked'

export const NOT_TRACKED_EXPLANATION =
  'Orders with no tracked visit to credit: the visitor declined analytics, the order was created on the server rather than in the browser, or the visit came before tracking was reliable. Direct visits are a channel of their own.'

/** The tooltip on a previous-period change that compares like with like. */
export const COMPARED_TITLE = 'Compared with the previous period'

/** The page's one baseline notice: what a greyed change is described by (`aria-describedby`). */
export const BASELINE_NOTICE_ID = 'ci-baseline-notice'

/** How a previous-period change is shown: its colour, its tooltip and, when greyed, the notice that says why. */
export interface DeltaPresentation {
  neutral: boolean
  title: string
  describedBy?: string
}

/** A change that compares like with like: coloured by what it means. */
export const LIKE_FOR_LIKE: DeltaPresentation = { neutral: false, title: COMPARED_TITLE }

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/
const DECIMAL = /^(\d+)(?:\.(\d+))?$/

/** A row the API credits to no tracked touch (`credit: 'unattributed'`); an older API's row never is. */
export function isNotTracked(row: { credit?: ConversionCredit }): boolean {
  return row.credit === 'unattributed'
}

/**
 * Whether a report reports Not tracked apart from everything else: its rows say their
 * credit (the coverage card's API), or — with no row to say so — its block carries
 * coverage, which comes with the credit. A report with neither, an older API's or a block
 * without coverage (A2's), still folds no-touch orders into the null row, and a page then
 * reads exactly as it did before.
 */
export function splitsNotTracked(report: { dataQuality: DataQuality | null; rows: readonly { credit?: ConversionCredit }[] }): boolean {
  if (report.rows.some((row) => row.credit !== undefined)) return true
  return report.rows.length === 0 && report.dataQuality?.coverage != null
}

/**
 * `rows` with the Not tracked rows (by `isLast`, `isNotTracked` unless named) moved to the
 * end, every other row in its order: the one rule for "Not tracked is always last".
 */
export function notTrackedLast<T>(rows: readonly T[], isLast: (row: T) => boolean = isNotTracked as (row: T) => boolean): T[] {
  return [...rows.filter((row) => !isLast(row)), ...rows.filter(isLast)]
}

/** "2026-10-04" → "4 Oct 2026"; null for anything that is not a day. */
export function formatBaselineDate(day: string | null | undefined): string | null {
  return day && ISO_DAY.test(day) ? formatDateKey(day, true) : null
}

/**
 * The API's tracked share, a decimal fraction as a string ("0.62"), as a whole percent
 * rounded half-up — worked out on the string's digits, so no floating-point rounding
 * can move it. Null for no share, or anything that is not a fraction from 0 to 1.
 */
export function trackedPercent(share: string | null | undefined): number | null {
  const match = share ? DECIMAL.exec(share) : null
  if (!match) return null
  const [, whole = '', digits = ''] = match
  // A share is at most 1: "1", "1.00" and "1.000" are, "1.004" is not.
  if (Number(whole) > 1 || (Number(whole) === 1 && /[1-9]/.test(digits))) return null
  const fraction = digits.padEnd(3, '0')
  return Number(whole) * 100 + Number(fraction.slice(0, 2)) + (Number(fraction[2]) >= 5 ? 1 : 0)
}

/** "62%", or null when there is no share to state. */
export function formatTrackedShare(share: string | null | undefined): string | null {
  const percent = trackedPercent(share)
  return percent === null ? null : `${percent}%`
}

export function isLowCoverage(percent: number): boolean {
  return percent < LOW_COVERAGE_PERCENT
}

/**
 * The notice a page shows when its range or its comparison starts before the baseline;
 * null when neither does, or when there is no baseline date to name.
 */
export function baselineNotice(dataQuality: DataQuality | null | undefined): string | null {
  if (!dataQuality || !(dataQuality.rangeBeforeBaseline || dataQuality.comparisonBeforeBaseline)) return null
  const date = formatBaselineDate(dataQuality.trackingReliableFrom)
  if (!date) return null
  return `Tracking on this site is reliable from ${date}. Figures before then are incomplete and comparisons across that date are not like-for-like.`
}

/** Whether a block has anything to say on a page: the baseline notice, or a share of orders tracked. */
export function hasDataQualityContent(dataQuality: DataQuality | null | undefined): boolean {
  return baselineNotice(dataQuality) !== null || trackedPercent(dataQuality?.coverage?.trackedShare) !== null
}

/** Whether the comparison reaches before the baseline: its changes are then shown neutral, never as good or bad. */
export function comparisonStraddles(dataQuality: DataQuality | null | undefined): boolean {
  return Boolean(dataQuality?.comparisonBeforeBaseline) && baselineNotice(dataQuality) !== null
}

/**
 * How a previous-period change is shown: neutral when the comparison straddles the
 * baseline, the notice its tooltip and its description (the page's notice, by id).
 */
export function deltaPresentation(dataQuality: DataQuality | null | undefined): DeltaPresentation {
  const notice = comparisonStraddles(dataQuality) ? baselineNotice(dataQuality) : null
  return notice ? { neutral: true, title: notice, describedBy: BASELINE_NOTICE_ID } : LIKE_FOR_LIKE
}

/** The coverage figure's explanation: how many orders, and what is not tracked. */
export function coverageTooltip(coverage: TrackingCoverage, percent: number, baseline: string | null): string {
  const date = formatBaselineDate(baseline)
  const before = date ? `before tracking on this site became reliable on ${date}` : 'before tracking on this site began'
  const low = isLowCoverage(percent)
    ? ` Below ${LOW_COVERAGE_PERCENT}%, the figures by channel describe only part of your sales.`
    : ''
  return `${formatInteger(coverage.trackedOrders)} of ${formatInteger(coverage.orders)} orders in this period (${percent}%) are credited to a tracked visit. Not tracked: visitors who declined analytics, orders created on the server rather than in the browser, and visits ${before}.${low}`
}
